/**
 * Parse Explorer
 * 
 * เครื่องมือสำรวจ Parse Server Collections และ Data
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Database,
  RefreshCcw,
  CheckCircle,
  XCircle,
  Loader2,
  Table as TableIcon,
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Settings,
  Zap,
} from 'lucide-react';
import {
  testConnection,
  getAllSchemas,
  getClassSchema,
  queryClass,
  getObjectById,
  PARSE_CONFIG,
} from '@/services/adapters/parse/parse-client';

// Schema field type colors
const fieldTypeColors: Record<string, string> = {
  String: 'bg-blue-500/10 text-blue-500',
  Number: 'bg-green-500/10 text-green-500',
  Boolean: 'bg-amber-500/10 text-amber-500',
  Date: 'bg-purple-500/10 text-purple-500',
  Object: 'bg-cyan-500/10 text-cyan-500',
  Array: 'bg-pink-500/10 text-pink-500',
  Pointer: 'bg-orange-500/10 text-orange-500',
  Relation: 'bg-rose-500/10 text-rose-500',
  File: 'bg-indigo-500/10 text-indigo-500',
  GeoPoint: 'bg-teal-500/10 text-teal-500',
};

interface SchemaInfo {
  className: string;
  fields: Record<string, { type: string; targetClass?: string }>;
}

export function ParseExplorer() {
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  
  // Schema state
  const [schemas, setSchemas] = useState<SchemaInfo[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSchema, setSelectedSchema] = useState<SchemaInfo | null>(null);
  
  // Data state
  const [data, setData] = useState<any[]>([]);
  const [dataCount, setDataCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail dialog
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Debug logs
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  
  // Test connection
  const handleTestConnection = async () => {
    setConnectionStatus('connecting');
    setDebugLogs([
      `🔌 Mode: ${import.meta.env.DEV ? 'Development (Proxy)' : 'Production (Direct)'}`,
      `📡 Primary: ${PARSE_CONFIG.serverURL}`,
      `📡 Alt: ${PARSE_CONFIG.altServerURL}`,
      `🎯 Target: ${PARSE_CONFIG.serverURL}`,
      `🔑 App ID: ***${PARSE_CONFIG.appId.slice(-8)}`,
      ``,
      `⏳ Trying endpoints...`,
    ]);
    
    try {
      const result = await testConnection();
      
      if (result.success) {
        setConnectionStatus('connected');
        setConnectionMessage(result.message);
        const schemaCount = result.serverInfo?.results?.length || 0;
        setDebugLogs(prev => [
          ...prev, 
          `✅ SUCCESS!`,
          `📍 Working URL: ${result.workingUrl}`,
          `📊 Found ${schemaCount} schemas`,
        ]);
        // Load schemas
        await loadSchemas();
      } else {
        setConnectionStatus('error');
        setConnectionMessage(result.message);
        setDebugLogs(prev => [
          ...prev, 
          `❌ ${result.message}`,
          ``,
          `💡 Tips:`,
          `   • Check Console (F12) for detailed logs`,
          `   • Verify App ID and Master Key`,
          `   • Make sure Parse Server is running`,
        ]);
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionMessage((error as Error).message);
      setDebugLogs(prev => [...prev, `💥 Error: ${(error as Error).message}`]);
    }
  };

  // Load schemas
  const loadSchemas = async () => {
    try {
      const schemaList = await getAllSchemas();
      setSchemas(schemaList);
      
      // Auto-select first class
      if (schemaList.length > 0) {
        setSelectedClass(schemaList[0].className);
      }
    } catch (error) {
      console.error('Error loading schemas:', error);
    }
  };

  // Load class data
  const loadClassData = async (className: string, pageNum: number = 1) => {
    if (!className) return;
    
    setLoading(true);
    try {
      // Get schema
      const schema = await getClassSchema(className);
      setSelectedSchema(schema);
      
      // Query data
      const result = await queryClass(className, {
        limit,
        skip: (pageNum - 1) * limit,
        order: '-createdAt',
      });
      
      setData(result.results || []);
      setDataCount(result.count || 0);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading class data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Effect: load data when class changes
  useEffect(() => {
    if (selectedClass && connectionStatus === 'connected') {
      loadClassData(selectedClass, 1);
    }
  }, [selectedClass, connectionStatus]);

  // View object detail
  const handleViewObject = async (objectId: string) => {
    try {
      const obj = await getObjectById(selectedClass, objectId);
      setSelectedObject(obj);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Error getting object:', error);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Export schema as JSON
  const exportSchema = () => {
    if (!selectedSchema) return;
    
    const blob = new Blob([JSON.stringify(selectedSchema, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schema-${selectedClass}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get field columns from schema
  const getFieldColumns = (): string[] => {
    if (!selectedSchema?.fields) return ['objectId', 'createdAt', 'updatedAt'];
    
    const systemFields = ['objectId', 'createdAt', 'updatedAt', 'ACL'];
    const customFields = Object.keys(selectedSchema.fields).filter(f => !systemFields.includes(f));
    
    return ['objectId', ...customFields.slice(0, 6), 'createdAt'];
  };

  // Format cell value
  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      if (value.__type === 'Date') return new Date(value.iso).toLocaleString('th-TH');
      if (value.__type === 'Pointer') return `→ ${value.className}:${value.objectId}`;
      if (value.__type === 'File') return value.name;
      return JSON.stringify(value).slice(0, 50);
    }
    if (typeof value === 'boolean') return value ? '✓' : '✗';
    if (typeof value === 'string' && value.length > 50) return value.slice(0, 50) + '...';
    return String(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            Parse Explorer
          </h1>
          <p className="text-muted-foreground">สำรวจ Collections และข้อมูลใน Parse Server</p>
        </div>
        
        {connectionStatus === 'connected' && (
          <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
            <CheckCircle className="h-3 w-3" />
            Connected
          </Badge>
        )}
      </div>

      {/* Connection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Parse Server Connection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Server URL</label>
              <Input value={PARSE_CONFIG.serverURL} readOnly className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">App ID</label>
              <Input 
                value={PARSE_CONFIG.appId ? '••••••••' + PARSE_CONFIG.appId.slice(-8) : 'Not configured'} 
                readOnly 
                className="mt-1 font-mono text-sm" 
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleTestConnection} 
              disabled={connectionStatus === 'connecting'}
              className="gap-2"
            >
              {connectionStatus === 'connecting' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
              {connectionStatus === 'connecting' ? 'Connecting...' : 'Connect'}
            </Button>
            
            {connectionStatus !== 'idle' && (
              <div className="flex items-center gap-2">
                {connectionStatus === 'connected' && <CheckCircle className="h-4 w-4 text-win" />}
                {connectionStatus === 'error' && <XCircle className="h-4 w-4 text-loss" />}
                <span className={connectionStatus === 'error' ? 'text-loss' : 'text-muted-foreground'}>
                  {connectionMessage}
                </span>
              </div>
            )}
          </div>
          
          {/* Debug Logs */}
          {debugLogs.length > 0 && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="text-xs font-mono space-y-1">
                {debugLogs.map((log, i) => (
                  <div key={i} className="text-muted-foreground">{log}</div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content - Only show when connected */}
      {connectionStatus === 'connected' && (
        <div className="grid gap-6 lg:grid-cols-4">
          {/* Sidebar - Collections List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TableIcon className="h-4 w-4" />
                Collections ({schemas.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <div className="p-2 space-y-1">
                  {schemas.map((schema) => (
                    <button
                      key={schema.className}
                      onClick={() => setSelectedClass(schema.className)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedClass === schema.className
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <div className="font-medium">{schema.className}</div>
                      <div className="text-xs opacity-70">
                        {Object.keys(schema.fields || {}).length} fields
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4">
            <Tabs defaultValue="data">
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="data">Data</TabsTrigger>
                  <TabsTrigger value="schema">Schema</TabsTrigger>
                </TabsList>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => loadClassData(selectedClass, page)}>
                    <RefreshCcw className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportSchema}>
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Data Tab */}
              <TabsContent value="data" className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{selectedClass}</CardTitle>
                        <CardDescription>{dataCount} records</CardDescription>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9 w-64"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {getFieldColumns().map((col) => (
                                  <TableHead key={col} className="whitespace-nowrap">
                                    {col}
                                  </TableHead>
                                ))}
                                <TableHead className="w-12"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {data.map((row) => (
                                <TableRow key={row.objectId}>
                                  {getFieldColumns().map((col) => (
                                    <TableCell key={col} className="max-w-[200px] truncate">
                                      {formatCellValue(row[col])}
                                    </TableCell>
                                  ))}
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleViewObject(row.objectId)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        
                        {/* Pagination */}
                        <div className="flex items-center justify-between mt-4">
                          <div className="text-sm text-muted-foreground">
                            Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, dataCount)} of {dataCount}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadClassData(selectedClass, page - 1)}
                              disabled={page <= 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadClassData(selectedClass, page + 1)}
                              disabled={page * limit >= dataCount}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Schema Tab */}
              <TabsContent value="schema">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedClass} Schema</CardTitle>
                    <CardDescription>Field definitions and types</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Field Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Target Class</TableHead>
                          <TableHead>Required</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedSchema && Object.entries(selectedSchema.fields || {}).map(([fieldName, fieldInfo]: [string, any]) => (
                          <TableRow key={fieldName}>
                            <TableCell>
                              <code className="text-sm bg-muted px-2 py-1 rounded">{fieldName}</code>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="secondary" 
                                className={`border-0 ${fieldTypeColors[fieldInfo.type] || 'bg-muted'}`}
                              >
                                {fieldInfo.type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {fieldInfo.targetClass || '-'}
                            </TableCell>
                            <TableCell>
                              {fieldInfo.required ? '✓' : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      {/* Object Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              {selectedClass} / {selectedObject?.objectId}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <pre className="text-sm font-mono bg-muted p-4 rounded-lg overflow-x-auto">
              {JSON.stringify(selectedObject, null, 2)}
            </pre>
          </ScrollArea>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(JSON.stringify(selectedObject, null, 2))}
              className="gap-2"
            >
              <Copy className="h-4 w-4" />
              Copy JSON
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

